import { STAFF_PERMISSIONS, type StaffPermission } from '@icebreaker/shared/staff';
import { ROUTES, SCREEN_ROUTES } from './routes';

/**
 * YETKİ GEREKTİREN PANEL YÜZEYLERİ — TEK ENVANTER.
 *
 * KAPATILAN SINIF (QA S2/S3 · P2-1): sunucu bir yeteneği 403'le reddederken
 * arayüz onu adminle BİREBİR AYNI çiziyordu. `moderator` yan gezinmede "Kurum
 * doğrulama"yı görüyor, tıklıyor, ekran açılıyor ve veri yerine "Kuyruk
 * yüklenemedi · Tekrar dene" geliyordu — hiçbir zaman başarılamayacak bir
 * döngü. Yetki bilgisi istemcide MEVCUTTU (`GET /panel/auth/me` beş anahtar
 * döndürüyor); eksik olan onu KULLANMAKTI (`selectHasStaffPermission`
 * yazılmıştı, tüketicisi sıfırdı).
 *
 * SEÇİLEN DAVRANIŞ — İKİSİ BİRDEN, gerekçesiyle:
 *  · GEZİNMEDE GİZLE. Kullanılamayacak bir yeteneği listelemek kullanıcıya
 *    yalan söylemektir; personel "bu ekran neden çalışmıyor" diye uğraşır.
 *  · ADRESE DOĞRUDAN GİDİLİRSE DÜRÜSTÇE SÖYLE. Gizlemek yetmez: eski sekme,
 *    yer imi ya da paylaşılan bağlantı ekranı yine açar. O yüzde "geçici hata +
 *    tekrar dene" DEĞİL, kalıcı ve dürüst bir "yetkin yok" yüzeyi çizilir ve
 *    personel kullanabileceği bir ekrana yönlendirilir. Tek başına gizlemek
 *    md. 1c'nin karşı ayağını (kullanıcı oraya nasıl olsa düşebilir) boş
 *    bırakırdı.
 *
 * BU BİR GÜVENLİK SINIRI DEĞİLDİR. Gerçek sınır `StaffPermissionsGuard`tır;
 * burada verilen karar yalnız "çiz / çizme"dir. Kaynak da sunucudur: yetki
 * listesi `GET /panel/auth/me` yanıtından gelir, istemcide rol → yetki tablosu
 * YENİDEN HESAPLANMAZ.
 *
 * `screen-permission-coverage.guard.spec.tsx` bu envanteri sunucunun
 * `@RequireStaffPermission` bildirimleriyle KARŞILAŞTIRIR: yeni bir ekran ya da
 * yetki isteyen yeni bir uç eklenip buraya yazılmazsa test KIRILIR.
 */

/** Yetki kararı verilen bir panel yüzeyi. */
export interface PermissionSurface {
  /** Kapı testlerinin ve kabuğun kullandığı kararlı kimlik. */
  readonly id: string;
  /** Sunucunun aynı yüzey için istediği yetki. */
  readonly permission: StaffPermission;
  /**
   * Yüzeyin sürdüğü panel denetleyici önekleri (`@Controller('panel/…')`).
   * Kapı bu önekleri API kaynağında bulup istenen yetkiyi ÖLÇER.
   */
  readonly endpoints: readonly string[];
  /**
   * Alt yüzeyler için: kapının SARILDIĞI dosya. `screen-permission-coverage`
   * bu dosyada gerçekten `PermissionSurfaceGate` olduğunu okur — kapı kaldırılıp
   * pano çıplak bırakılırsa test KIRILIR. Ekranlarda `undefined`: onların kapısı
   * `router.tsx`te sayılır.
   */
  readonly mountedIn?: string;
}

/** Ekran rotası → yüzey. `SCREEN_ROUTES` ile BİREBİR eşleşmek zorunda. */
export const SCREEN_PERMISSIONS: Readonly<Record<string, PermissionSurface>> = {
  [ROUTES.home]: {
    id: 'screen:overview',
    permission: STAFF_PERMISSIONS.MODERATE,
    endpoints: ['panel/overview', 'panel/stats'],
  },
  [ROUTES.users]: {
    id: 'screen:users',
    permission: STAFF_PERMISSIONS.MODERATE,
    endpoints: ['panel/users'],
  },
  [ROUTES.moderation]: {
    id: 'screen:moderation',
    permission: STAFF_PERMISSIONS.MODERATE,
    endpoints: ['panel/moderation'],
  },
  [ROUTES.reports]: {
    id: 'screen:reports',
    permission: STAFF_PERMISSIONS.MODERATE,
    endpoints: ['panel/moderation'],
  },
  [ROUTES.verifications]: {
    id: 'screen:verifications',
    permission: STAFF_PERMISSIONS.ORGANIZATION_VERIFICATION_DECIDE,
    endpoints: ['panel/organization-verifications'],
  },
  /*
   * E-posta gönderim defteri: `panel/email-deliveries` TEK işleyicisi
   * `staff:audit_read` ister. Katalogdaki bu anahtar bugüne kadar
   * TÜKETİCİSİZDİ; ilk tüketicisi budur ve yalnız `admin` taşır.
   */
  [ROUTES.emailDeliveries]: {
    id: 'screen:email-deliveries',
    permission: STAFF_PERMISSIONS.AUDIT_READ,
    endpoints: ['panel/email-deliveries'],
  },
  /*
   * Personel yönetimi: `panel/staff` denetleyicisinin altı işleyicisi de
   * `staff:account_manage` ister ve o yetki YALNIZ `admin` rolündedir —
   * `moderator` bu bağlantıyı görmez, adrese giderse dürüst yüzeyi görür.
   */
  [ROUTES.staff]: {
    id: 'screen:staff',
    permission: STAFF_PERMISSIONS.ACCOUNT_MANAGE,
    endpoints: ['panel/staff'],
  },
  /*
   * Satış adayları ve kurumlar (kurum satışla açılır · V3): ikisi de yalnız
   * `admin` rolündedir. `moderator` bağlantıları görmez, adrese giderse
   * dürüst "yetkin yok" yüzeyini görür. Kurum DETAYI aynı kapıyı taşır
   * (`router.tsx`).
   */
  [ROUTES.salesInquiries]: {
    id: 'screen:sales-inquiries',
    permission: STAFF_PERMISSIONS.SALES_INQUIRY_MANAGE,
    endpoints: ['panel/sales-inquiries'],
  },
  [ROUTES.organizations]: {
    id: 'screen:organizations',
    permission: STAFF_PERMISSIONS.ORGANIZATION_PROVISION,
    endpoints: ['panel/organizations'],
  },
  /*
   * Referans (hediye) kodları (P7): `panel/referral-codes` denetleyicisinin üç
   * işleyicisi de `staff:credit_manage` ister (kod para tanımlar, kredi
   * yükleyenle aynı insan yönetir). `moderator` bağlantıyı görmez.
   */
  [ROUTES.referralCodes]: {
    id: 'screen:referral-codes',
    permission: STAFF_PERMISSIONS.CREDIT_MANAGE,
    endpoints: ['panel/referral-codes'],
  },
};

/**
 * EKRAN İÇİ ALT YÜZEYLER — rotası yok ama yetkisi VAR.
 *
 * Sınıf taraması bunları ekranlarla birlikte buldu (md. 1): moderasyon
 * kuyruğunda bir SAHİPLENME kalemi açıldığında karar panosu çiziliyor ve
 * `panel/claims` uçlarını sürüyor. O uçlar `staff:claim_decide` istiyor ve
 * `moderator` onu TAŞIMIYOR — yani P2-1'in ta kendisi, ekran değil pano
 * ölçeğinde. Kuyruğu görmek (`staff:moderate`) kararı vermeye yetmez.
 */
export const SUB_SURFACE_PERMISSIONS: readonly PermissionSurface[] = [
  {
    id: 'panel:claim-decision',
    permission: STAFF_PERMISSIONS.CLAIM_DECIDE,
    endpoints: ['panel/claims'],
    mountedIn: 'apps/admin/src/features/admin/ui/moderation-screen.tsx',
  },
  /*
   * KURUM DETAYINDAKİ KREDİ BÖLÜMLERİ: kurum ekranı `staff:organization_provision`
   * ister, kredi uçları AYRI yetki ister (`staff:credit_manage`). Bugün ikisi de
   * yalnız `admin`dedir ama yetki tablosu ayrışırsa kredi özeti, sözleşme ve
   * defter çizilmez, künye ve sahip bloğu çalışmaya devam eder.
   */
  {
    id: 'panel:organization-credits',
    permission: STAFF_PERMISSIONS.CREDIT_MANAGE,
    endpoints: ['panel/organizations/:organizationId/credits'],
    mountedIn: 'apps/admin/src/features/organizations/ui/organization-detail-screen.tsx',
  },
  /*
   * KURUM DETAYINDAKİ "KAPAĞI KALDIR" EYLEMİ (kurum kapağı · Story P1): kapak
   * önizlemesi kurum ekranının yetkisiyle görünür, kaldırma AYRI yetki ister
   * (`staff:organization_cover_remove`). Yetkisi olmayan personelde düğme
   * çizilmez, önizleme yerinde kalır.
   */
  {
    id: 'panel:organization-cover-removal',
    permission: STAFF_PERMISSIONS.ORGANIZATION_COVER_REMOVE,
    endpoints: ['panel/organizations/:organizationId/cover'],
    mountedIn: 'apps/admin/src/features/organizations/ui/organization-cover-card.tsx',
  },
  /*
   * KURUM DETAYINDAKİ "ETKİNLİK RAPORLARI" BÖLÜMÜ (organizatör raporu Story 5):
   * bugün kurum ekranıyla aynı yetki (`staff:organization_provision`), ama kendi
   * kapısıyla monte edilir: yetki tablosu ayrışırsa bölüm çizilmez, uç 403 döner.
   */
  {
    id: 'panel:organization-event-reports',
    permission: STAFF_PERMISSIONS.ORGANIZATION_PROVISION,
    endpoints: ['panel/organizations/:organizationId/event-reports'],
    mountedIn: 'apps/admin/src/features/organizations/ui/organization-detail-screen.tsx',
  },
  /*
   * KURUM DETAYINDAKİ "ETKİNLİK SONRASI" BÖLÜMÜ (belge ve anket Story 8): kurum
   * ekranıyla aynı yetki (`staff:organization_provision`), kendi kapısıyla monte
   * edilir. Yanıt yalnız durum ve 5'in katı cevap sayısıdır.
   */
  {
    id: 'panel:organization-post-event',
    permission: STAFF_PERMISSIONS.ORGANIZATION_PROVISION,
    endpoints: ['panel/organizations/:organizationId/post-event'],
    mountedIn: 'apps/admin/src/features/organizations/ui/organization-detail-screen.tsx',
  },
  /*
   * BÖLÜMDEKİ "ANKETİ KAPAT" EYLEMİ (belge ve anket Story 8): kötüye kullanılan
   * anketi kapatmak bir moderasyon kararıdır (`staff:moderate`, uç A1'den beri
   * bu yetkiyle). Bölümü görmek kapatmaya yetmez; yetkisi olmayan personelde
   * düğme ve diyalog DOM'a girmez.
   */
  {
    id: 'panel:event-survey-disable',
    permission: STAFF_PERMISSIONS.MODERATE,
    endpoints: ['panel/events'],
    mountedIn: 'apps/admin/src/features/organizations/ui/organization-post-event-section.tsx',
  },
  /*
   * "Kurum aç" diyaloğundaki "İlişkili satış adayı" seçicisi satış adayı
   * listesini okur (`staff:sales_inquiry_manage`). Yetkisi olmayan personelde
   * seçici çizilmez, kurum yine açılabilir.
   */
  {
    id: 'panel:create-organization-lead-picker',
    permission: STAFF_PERMISSIONS.SALES_INQUIRY_MANAGE,
    endpoints: ['panel/sales-inquiries'],
    mountedIn: 'apps/admin/src/features/organizations/ui/create-organization-dialog.tsx',
  },
];

/**
 * KİMLİK/BENLİK UÇLARI — beyanlı muafiyet.
 *
 * `panel/auth` altındaki uçlar ya kimliksizdir (login/verify/setup/refresh) ya
 * da HER personelin taban yetkisiyle koşar (`staff:self_*`). Yüzey kararı
 * "yetkin var mı" değil "oturumun var mı"dır ve onu `StaffGate` verir.
 */
export const SELF_OR_PUBLIC_ENDPOINTS: readonly string[] = ['panel/auth'];

/** Envanterdeki tüm yüzeyler (ekran + alt yüzey), kapıların tek girdisi. */
export const PERMISSION_SURFACES: readonly PermissionSurface[] = [
  ...SCREEN_ROUTES.map((route) => SCREEN_PERMISSIONS[route]).filter(
    (surface): surface is PermissionSurface => surface !== undefined,
  ),
  ...SUB_SURFACE_PERMISSIONS,
];

/** Rotanın istediği yetki; envanterde yoksa `undefined` (kapı bunu KIRAR). */
export function screenPermission(route: string): StaffPermission | undefined {
  return SCREEN_PERMISSIONS[route]?.permission;
}

/** Alt yüzeyin istediği yetki; envanterde yoksa `undefined` (kapı KAPALI). */
export function subSurfacePermission(id: string): StaffPermission | undefined {
  return SUB_SURFACE_PERMISSIONS.find((surface) => surface.id === id)?.permission;
}
